import * as React from 'react';
import { Autocomplete, Box, Divider, FormControl, FormControlLabel, Link, List, ListItemButton, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { AxiosService } from '../../Services/AxiosService';
import { useAuth } from 'react-oidc-context';
import FileInput from '../FileInput/FileInput';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { GlobalContext } from '../../Context/GlobalContext';
import useAxios from '../../Hooks/useAxios';
import { DATE_FORMAT, HRO_ALPS } from '../../Constants/Global';


/**
 * A form component for displaying and managing subprocess details. It includes fields for department, responsible,
 * employee, end date, and status, as well as functionality for uploading and displaying files.
 *
 * @component
 * @example
 * <SubprocessForm subProcessData={subProcessData} />
 * @param {Object} props - The component props.
 * @param {Object} props.subProcessData - The subprocess data to display in the form.
 * @param {string} props.subProcessData.department - The department associated with the subprocess.
 * @param {string} props.subProcessData.responsible - The responsible person for the subprocess.
 * @param {string} props.subProcessData.targetEmployee - The employee associated with the subprocess.
 * @param {string} props.subProcessData.endDate - The end date of the subprocess.
 * @param {number} props.subProcessData.statusId - The status ID of the subprocess.
 * @param {Array} props.subProcessData.files - The list of files associated with the subprocess.
 * @param {number} props.subProcessData.duration - The duration of the subprocess in days.
 * @returns {JSX.Element} The rendered subprocess form component.
 */

 let targetEmployeeALPSCodeResult = "";
 let targetEmployeeALPSCodeValue = "";
 let id = 0;




function SubprocessForm(props) {
  const { subProcessData } = props
  const { userProperties, setAlertBarSeverity, setAlertBarMessage, setAlertBarOpen } = React.useContext(GlobalContext);
  const auth = useAuth();
  const [files, setFiles] = React.useState(subProcessData.files);
  const { data, isLoading, error } = useAxios("processStatuses");
  const [statuses, setStatuses] = React.useState([]);

  const [targetEmployeeALPSCode, setTargetEmployeeALPSCode] = React.useState(subProcessData.targetEmployeeALPSCode || "");
  const [targetEmployeeALPSCodeError, setTargetEmployeeALPSCodeError] = React.useState(false);

  //console.log("subProcessData.typeId: ", subProcessData.typeId);


  let bIsALPSModalSelected = false;
  if( subProcessData.typeId === HRO_ALPS)
    bIsALPSModalSelected = true;

  let isInProgress = true;
  if(subProcessData.statusId == 2 )
    isInProgress = false;
  let textColor = isInProgress
    ? "rgba(0,0,0,0.38)"
    : "#000";

  id = subProcessData.targetEmployeeId;



  /**
   * Fetches process statuses from the API and updates the state. Displays an error message if the request fails.
   */
  React.useEffect(() => {
    if (!isLoading) {
      if (error) {
        setAlertBarSeverity("error");
        setAlertBarMessage(error);
        setAlertBarOpen(true);
      } else {
        setStatuses(data);
      }
    }
  }, [isLoading]);

  /**
   * Renders radio buttons for each process status.
   *
   * @returns {Array} An array of `FormControlLabel` components representing the process statuses.
   */
  const displayRadioButtonStatus = () => {
    return [...statuses.map((status) =>{
      return <FormControlLabel
          value={status.id}
          control={<Radio />}
          label={status.name}
          disabled
          labelPlacement="bottom"
          sx={{margin: "auto"}}
        />
    })]
  }

  /**
   * Renders the list of files associated with the subprocess and a file upload input.
   *
   * @returns {Array} An array of `ListItemButton` components representing the files and a `FileInput` component.
   */
  const displayFiles = () => {
    return subProcessData.departmentId === userProperties.departmentId &&
      // || subProcessData.responsibleId === userProperties.id
      [(<><br/><Divider variant="middle"/><FileInput handleSelect={handleSelect}/></>),
      ...files.map((file) =>{
        return <ListItemButton>
          <Link href={ file.signedUrl } target="_blank" underline="none" sx={{ width: "100%" }}>
            <AttachFileIcon sx={{ position: "relative", top: "6px" }}/>{ file.filename }
          </Link>
        </ListItemButton>
      })]
  }

  /**
   * Handles the selection of a file for upload.
   *
   * @param {Event} event - The file input change event.
   */
  const handleSelect = (event) => {
    uploadFile(event.target.files[0]);
  };


  /**
   * Uploads a file to the server and updates the list of files.
   *
   * @param {File} file - The file to upload.
   */
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    AxiosService.post_file(auth.user?.access_token, subProcessData.id, formData)
    .then(response => {
      setAlertBarSeverity("success");
      setAlertBarMessage(response.data.message);
      setAlertBarOpen(true);
      let filtered = files.filter(item => item.filename !== file.name);
      setFiles([...filtered, { filename: file.name, signedUrl: response.data.signedUrl, mimeType: file.type }]);
    })
    .catch(error => {
      setAlertBarSeverity("error");
      setAlertBarMessage(error);
      setAlertBarOpen(true);
    });
  };

    //targetEmployeeALPSCodeValue= subProcessData.targetEmployeeALPSCode;

  return (

    <
    Box
   sx={{

   minHeight: '10vh',
           maxHeight: '30vh',
           overflowY: 'auto',

   }}
      >
      <Typography color="grey" fontSize="14px" sx={{position: "relative", bottom: "4px", userSelect: "none"}}>Duration: {subProcessData.duration} day{subProcessData.duration > 1 && "s"}</Typography>

      <Autocomplete
        disablePortal
        disabled
        value={subProcessData.department}
        sx={{
          width: "100%",
          marginTop: "10px",

          "& .MuiInputLabel-root": {
            color: textColor
          },

          "& .MuiInputLabel-root.Mui-disabled": {
            color: textColor
          },

          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: textColor
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Department"
          />
        )}
      />

      <Autocomplete
        disablePortal
        disabled
        value={subProcessData.responsible}
        sx={{
          width: "100%",
          marginTop: "10px",

          "& .MuiInputLabel-root": {
            color: textColor
          },

          "& .MuiInputLabel-root.Mui-disabled": {
            color: textColor
          },

          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: textColor
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Responsible"
          />
        )}
      />


      { !bIsALPSModalSelected &&

        <Autocomplete
          disablePortal
          disabled
          value={subProcessData.targetEmployeeALPSCode}
          sx={{
            width: "100%",
            marginTop: "10px",

            "& .MuiInputLabel-root": {
              color: textColor
            },

            "& .MuiInputLabel-root.Mui-disabled": {
              color: textColor
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: textColor
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="ALPS"
            />
          )}
        />

      }

      { bIsALPSModalSelected &&
        <TextField

          label="ALPS"
          value={targetEmployeeALPSCode}
          onChange={(event) => {
            const value0 = event.target.value;

            setTargetEmployeeALPSCode(value0);
            setTargetEmployeeALPSCodeResult(value0);
          }}
          InputLabelProps={{
            sx: {
              color: textColor
            }
          }}
          sx={{
            width: "100%",
            marginTop: "10px",

            "& .MuiInputLabel-root": {
              color: textColor
            },

            "& .MuiInputLabel-root.Mui-disabled": {
              color: textColor
            },

            "& .MuiInputBase-input": {
              color: textColor
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: textColor
            }
          }}
        />
        }

        <Autocomplete
         disablePortal
         disabled
         value={subProcessData.targetEmployee}
         sx={{
           width: "100%",
           marginTop: "10px",

           "& .MuiInputLabel-root": {
             color: textColor
           },

           "& .MuiInputLabel-root.Mui-disabled": {
             color: textColor
           },

           "& .MuiInputBase-input.Mui-disabled": {
             WebkitTextFillColor: textColor
           }
         }}
         renderInput={(params) => (
           <TextField
             {...params}
             label="Employee"
           />
         )}
       />

       <LocalizationProvider dateAdapter={AdapterDayjs}>
         <Box pt={"10px"}>
           <DatePicker
             disabled
             format={DATE_FORMAT}
             label="End date"
             defaultValue={dayjs(subProcessData.endDate, DATE_FORMAT)}
             sx={{
               width: "100%",
               marginTop: "10px",

               "& .MuiInputLabel-root": {
                 color: textColor
               },

               "& .MuiInputLabel-root.Mui-disabled": {
                 color: textColor
               },

               "& .MuiInputBase-input.Mui-disabled": {
                 WebkitTextFillColor: textColor
               },

               "& .MuiSvgIcon-root": {
                 color: textColor
               }
             }}
           />
         </Box>
       </LocalizationProvider>





      <FormControl sx={{width: "100%"}}>
        <RadioGroup
          row
          aria-labelledby="status-radio-buttons-group-label"
          defaultValue={subProcessData.statusId}
          name="radio-buttons-group"
        >
          {displayRadioButtonStatus()}
        </RadioGroup>
      </FormControl>
      <List>
        {displayFiles()}
      </List>
  </Box>
  );
}

export const setTargetEmployeeALPSCodeResult = (value) => {
  targetEmployeeALPSCodeResult = value
}

export const getTargetEmployeeALPSCode = () => {
  return targetEmployeeALPSCodeResult;
}

export const getEmployeeId = () => {
  return id;
}

export default SubprocessForm;
